import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InvoicingWorkspace } from '@/features/invoicing';
import { SalaryWorkspace } from '@/features/salary';
import { ExtrasWorkspace } from '@/features/extras';
import { type Language } from '@/types';

interface EconomyWorkspaceProps {
  language: Language;
}

type EconomyTab = 'fakturering' | 'loner' | 'extra';

const TAB_LABELS: Record<EconomyTab, { sv: string; en: string }> = {
  fakturering: { sv: 'Fakturering', en: 'Invoicing' },
  loner: { sv: 'Löner', en: 'Payroll' },
  extra: { sv: 'Extra', en: 'Extras' },
};

const VALID_TABS: EconomyTab[] = ['fakturering', 'loner', 'extra'];

function readTabFromHash(): EconomyTab {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  const segments = hash.split('/').filter(Boolean);
  // segments[0] === 'economy', segments[1] is the tab (strip any ?query a child appended)
  const sub = (segments[1]?.split('?')[0]) as EconomyTab | undefined;
  return sub && VALID_TABS.includes(sub) ? sub : 'fakturering';
}

export function EconomyWorkspace({ language }: EconomyWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<EconomyTab>(readTabFromHash);

  // Sync tab when the hash changes externally (e.g. back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#/', '').replace('#', '');
      const segments = hash.split('/').filter(Boolean);
      if (segments[0] === 'economy') {
        const sub = (segments[1]?.split('?')[0]) as EconomyTab | undefined;
        const next = sub && VALID_TABS.includes(sub) ? sub : 'fakturering';
        setActiveTab(next);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleTabChange = (tab: string) => {
    const t = tab as EconomyTab;
    setActiveTab(t);
    window.location.hash = `/economy/${t}`;
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="mb-6">
        {VALID_TABS.map((tab) => (
          <TabsTrigger key={tab} value={tab}>
            {TAB_LABELS[tab][language]}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="fakturering">
        <InvoicingWorkspace language={language} />
      </TabsContent>

      <TabsContent value="loner">
        <SalaryWorkspace language={language} />
      </TabsContent>

      <TabsContent value="extra">
        <ExtrasWorkspace language={language} />
      </TabsContent>
    </Tabs>
  );
}
